import { OptionalWhitepaperSlug } from '@/modules/Whitepapers/whitepapers';

import { RedirectionPreAction } from './urlHelper';

export interface BasicInfoTitle {
  smTitle: string;
  mainTitle: string;
}

export interface VerifyEmailCopy {
  title: string;
  description: string;
  resendMessage: string;
  buttonText: string;
}

export interface RegisterCopy {
  basicInfo: BasicInfoTitle;
  simple: string;
  subscriptions: string;
  verifyEmail: VerifyEmailCopy;
}

/**
 * Gets the conditional copy for the Registration flow
 *
 * @param action defines where the user will redirect to. Used for the initial title
 * @param whitepaperSlug the identifier of the whitepaper that user has requested
 * @returns an Object with the titles of each RegisterStage
 */
export const getRegisterCopy = (
  action: RedirectionPreAction | null,
  whitepaperSlug: OptionalWhitepaperSlug,
): RegisterCopy => ({
  basicInfo: getBasicInfoTitle(action),
  simple: whitepaperSlug ? 'Help us get to know you' : "Let's find the right job for you",
  subscriptions: 'Thanks, almost done!',
  verifyEmail: getVerifyEmailCopy(whitepaperSlug),
});

/**
 * Get's the initial title string for the Registration flow,
 * depending on where the user is to be redirected to afterwards.
 *
 * @param action defines where the user will redirect to
 * @returns an Object with two titles. One main and one small
 */
export const getBasicInfoTitle = (action: RedirectionPreAction | null): BasicInfoTitle => {
  switch (action) {
    case 'enquire_job':
      return { smTitle: 'Welcome!', mainTitle: 'Enquire about this job by signing up' };
    case 'save_job':
      return { smTitle: 'Welcome!', mainTitle: 'Save this job by signing up' };
    case 'create_job_alert':
      return { smTitle: 'Welcome back!', mainTitle: 'Save this job alert by signing up' };
    case 'whitepaper':
      return { smTitle: '', mainTitle: 'Sign in, or create an account to access our resources' };
    default:
      return { smTitle: 'First things first', mainTitle: "Let's get you on board" };
  }
};

/**
 * Gets the copy for the verify email modal at the end of the registration flow depending on
 * whether the user has requested a whitepaper or not.
 *
 * @param whitepaperSlug the identifier of the whitepaper that the user has requested
 * @returns An object of the copy for the Verify Email modal that pops up at the end of the registration flow
 */
const getVerifyEmailCopy = (whitepaperSlug: OptionalWhitepaperSlug): VerifyEmailCopy => {
  return whitepaperSlug
    ? {
        title: `Check your inbox.\nYour document is on its way!`,
        description: `We've sent you an email to verify it's you. Open it up, click the link and login to be a part of the Medrecruit community!`,
        resendMessage: `Didn't receive an email?\n Check your junk folder or resend.`,
        buttonText: 'Resend verification email',
      }
    : {
        title: 'Verify your email address',
        description: `We've sent you a verification email. Open it up, click the link and login. That's it! You're part of the Medrecruit community.`,
        resendMessage: `Didn't receive an email? Check your junk folder or select 'resend'.`,
        buttonText: 'Resend verification email',
      };
};
